import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaLock, FaArrowRight } from 'react-icons/fa';

const ADMIN_PASSWORD = 'perspective2024'; // Change this to your desired password

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already authenticated
    const authStatus = localStorage.getItem('admin_authenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (password === ADMIN_PASSWORD) {
      localStorage.setItem('admin_authenticated', 'true');
      setIsAuthenticated(true);
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_authenticated');
    setIsAuthenticated(false);
    setPassword('');
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-perspective-carbon flex items-center justify-center">
        <div className="animate-pulse text-xl text-perspective-cloud/80">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-perspective-carbon text-perspective-sand relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-60">
          <div className="absolute inset-0 bg-grid-soft [background-size:28px_28px] opacity-20" />
        </div>

        <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
          <motion.div
            className="w-full max-w-md bg-white/5 backdrop-blur border border-white/10 rounded-3xl shadow-elevated p-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-perspective-sage/15 border border-perspective-sage/30 mb-4">
                <FaLock className="text-perspective-sage text-2xl" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Admin Access</h1>
              <p className="text-perspective-cloud/80">Enter password to access admin dashboard</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-perspective-cloud/90 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-perspective-cloud/50 focus:outline-none focus:ring-2 focus:ring-perspective-sage focus:border-transparent transition-all"
                  placeholder="Enter admin password"
                  autoFocus
                />
              </div>

              {error && (
                <motion.div
                  className="p-3 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 text-sm"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {error}
                </motion.div>
              )}

              <button
                type="submit"
                className="w-full group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-perspective-sage text-perspective-carbon font-semibold hover:shadow-xl transition-all duration-300 shadow-lg hover:scale-[1.02]"
              >
                <span>Access Dashboard</span>
                <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/')}
                className="text-sm text-perspective-cloud/70 hover:text-perspective-sage transition-colors"
              >
                ← Back to Home
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={handleLogout}
          className="px-4 py-2 rounded-full bg-white/5 backdrop-blur border border-white/10 text-perspective-cloud/80 hover:text-perspective-sage hover:border-perspective-sage transition-all text-sm font-medium"
        >
          Logout
        </button>
      </div>
    </>
  );
};

export default ProtectedRoute;
